<template>
  <!-- 登录页面 -->
  <div v-if="$route.path === '/login'" class="login-container">
    <router-view></router-view>
  </div>
  
  <!-- 移动端独立页面 -->
  <div v-else-if="isMobileStandalone" class="mobile-standalone-container">
    <router-view></router-view>
  </div>
  
  <!-- 嵌入模式（组态预览等） -->
  <div v-else-if="isEmbedded" class="embedded-container">
    <router-view></router-view>
  </div>
  
  <!-- 完整布局 -->
  <div v-else class="app-container">
    <!-- 系统说明 -->
    <div class="system-info">
      <div class="system-header">
        <div class="system-logo">
          <img src="@/assets/images/logo.png" class="logo-icon" alt="logo" />
          <div class="logo-text">
              <h1>村镇污水智慧运维一体化平台</h1>
              <p>数据更新：{{ updateTime }}</p>
            </div>
        </div>
        
        <!-- 顶部菜单 -->
        <div class="top-menu">
          <el-menu
            :default-active="activeMenu"
            class="el-menu-demo"
            mode="horizontal"
            @select="handleMenuSelect"
          >
            <!-- 动态菜单 -->
            <template v-for="menu in menuList">
              <el-submenu v-if="menu.children && menu.children.length > 0 && menu.meta && !menu.meta.hidden" :index="menu.path" :key="menu.path">
                <template slot="title">
                  <span>{{ menu.meta.title || '' }}</span>
                </template>
                <template v-for="subMenu in menu.children">
                  <el-submenu v-if="subMenu.children && subMenu.children.length > 0 && subMenu.meta && !subMenu.meta.hidden" :index="menu.path + '/' + subMenu.path" :key="subMenu.path">
                    <template slot="title">
                      <span>{{ subMenu.meta.title || '' }}</span>
                    </template>
                    <el-menu-item
                      v-for="subSubMenu in subMenu.children.filter(item => item.meta && !item.meta.hidden)"
                      :key="subSubMenu.path"
                      :index="menu.path + '/' + subMenu.path + '/' + subSubMenu.path"
                    >
                      <span>{{ subSubMenu.meta.title || '' }}</span>
                    </el-menu-item>
                  </el-submenu>
                  <el-menu-item v-else-if="subMenu.meta && !subMenu.meta.hidden" :index="menu.path + '/' + subMenu.path" :key="subMenu.path">
                    <span>{{ subMenu.meta.title || '' }}</span>
                  </el-menu-item>
                </template>
              </el-submenu>
              <el-menu-item v-else-if="menu.meta && !menu.meta.hidden" :index="menu.path" :key="menu.path">
                <span>{{ menu.meta.title || '' }}</span>
              </el-menu-item>
            </template>
          </el-menu>
        </div>
        
        <div class="system-actions">
          <div class="user-info">
            <el-dropdown @command="handleDropdownCommand">
              <span class="user-avatar">
                <img src="@/assets/images/user-avatar.svg" alt="用户头像">
                <div class="user-details">
                  <span class="user-name">{{ user.name }}</span>
                  <span class="user-role" :class="getRoleClass(user.roleName)">{{ user.roleName }}</span>
                </div>
                <i class="el-icon-arrow-down"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
<!--                <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="settings">设置</el-dropdown-item>-->
                <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 标签页区域 -->
    <div class="tab-container" v-if="tabs.length > 0">
      <div class="tab-scroll-wrapper">
        <el-tabs v-model="activeTab" @tab-click="switchTab">
          <el-tab-pane
            v-for="tab in tabs"
            :key="tab.path"
            :label="tab.title"
            :name="tab.path"
          >
            <template slot="label">
              <div class="tab-label">
                <i :class="tab.icon || 'el-icon-s-home'"></i>
                <span>{{ tab.title }}</span>
                <el-button 
                  type="text" 
                  size="mini" 
                  class="tab-close"
                  @click.stop="closeTab(tab.path)"
                >
                  ×
                </el-button>
              </div>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>
      <div class="tab-actions">
        <el-tooltip content="向左滚动" placement="bottom" :open-delay="300">
          <button class="tab-action-btn" @click="scrollTabs('left')">
            <i class="el-icon-arrow-left"></i>
          </button>
        </el-tooltip>
        <el-tooltip content="向右滚动" placement="bottom" :open-delay="300">
          <button class="tab-action-btn" @click="scrollTabs('right')">
            <i class="el-icon-arrow-right"></i>
          </button>
        </el-tooltip>
        <el-tooltip content="关闭全部" placement="bottom" :open-delay="300">
          <button class="tab-action-btn" @click="closeAllTabs">
            <i class="el-icon-close"></i>
          </button>
        </el-tooltip>
      </div>
    </div>
    
    <!-- 主内容区 -->
    <main class="main-content">
      <!-- 页面内容 -->
      <div class="page-content">
        <keep-alive :include="appLevelCachedNames">
          <router-view></router-view>
        </keep-alive>
      </div>
    </main>

    <!-- 修改密码弹窗 -->
    <change-password-dialog :visible.sync="changePasswordVisible" />
  </div>
</template>

<script>
import { initTheme } from './utils/theme'
import { connectAlarmWebSocket, disconnectAlarmWebSocket, offAlarmMessage, onAlarmMessage } from './utils/alarmWebSocket'
import ChangePasswordDialog from '@/components/ChangePasswordDialog.vue'

export default {
  name: 'App',
  components: {
    ChangePasswordDialog
  },
  computed: {
    user() {
      return this.$store.getters.user
    },
    menuList() {
      return this.$store.getters.menuList
    },
    activeMenu() {
      const path = this.$route.path
      // 找到当前路由对应的菜单索引
      const findMenuIndex = (menuList, currentPath) => {
        for (const menu of menuList) {
          if (menu.children && menu.children.length > 0) {
            for (const subMenu of menu.children) {
              const fullPath = menu.path + '/' + subMenu.path
              if (currentPath.startsWith(fullPath)) {
                return fullPath
              }
            }
          } else if (currentPath.startsWith(menu.path)) {
            return menu.path
          }
        }
        return '1'
      }
      return findMenuIndex(this.menuList, path)
    },
    isEmbedded() {
      return this.$route.query.embedded === 'true'
    },
    isMobileStandalone() {
      return this.$route.matched.some(function (record) {
        return record.meta && record.meta.standalone
      })
    },
    // App 级 keep-alive 缓存列表：缓存 KeepAliveView（嵌套路由父组件）和需要缓存的独立页面
    appLevelCachedNames() {
      const names = ['KeepAliveView']
      // 如果有打开的 tab 指向首页（IntegratedMonitoring），也缓存它
      const hasHomeTab = this.tabs.some(tab => tab.path.split('?')[0] === '/home')
      if (hasHomeTab) {
        names.push('IntegratedMonitoring')
      }
      return names
    }
  },
  data() {
    return {
      updateTime: '',
      tabs: [],
      activeTab: '',
      changePasswordVisible: false
    }
  },
  watch: {
    $route(to) {
      if (to.path === '/login' || to.path === '/403' || to.path === '/404') {
        return
      }
      if (this.isMobileStandalone || this.isEmbedded) {
        return
      }
      this.addTabFromRoute(to)
    },
    tabs: {
      handler(tabs) {
        sessionStorage.setItem('app_tabs', JSON.stringify(tabs))
      },
      deep: true
    },
    activeTab(val) {
      sessionStorage.setItem('app_active_tab', val)
    }
  },
  mounted() {
    console.log('App mounted')
    console.log('User:', this.user)
    console.log('Menu list:', this.menuList)
    initTheme()
    this.initUser()
    this.updateTime = this.formatDateTime(new Date())
    this.timeInterval = setInterval(() => {
      this.updateTime = this.formatDateTime(new Date())
    }, 1000)
    this.restoreTabs()
    connectAlarmWebSocket()
    this._globalAlarmHandler = (data) => {
      if (data.type === 'alarm') {
        this.$notify({
          title: '告警通知',
          message: `${data.siteName || ''} ${data.content || '收到新的告警信息'}`,
          type: 'warning',
          duration: 5000
        })
      }
    }
    onAlarmMessage(this._globalAlarmHandler)
  },
  beforeDestroy() {
    // 组件销毁时清除定时器
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }
    if (this._globalAlarmHandler) {
      offAlarmMessage(this._globalAlarmHandler)
    }
    disconnectAlarmWebSocket()
  },
  methods: {
    formatDateTime(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    restoreTabs() {
      try {
        const savedTabs = sessionStorage.getItem('app_tabs')
        const savedActiveTab = sessionStorage.getItem('app_active_tab')
        if (savedTabs) {
          this.tabs = JSON.parse(savedTabs)
          // 补全旧数据中缺少的 componentName，并恢复缓存列表
          this.$store.commit('CLEAR_CACHED_VIEWS')
          this.tabs.forEach(tab => {
            if (!tab.componentName) {
              const pathOnly = tab.path.split('?')[0]
              const route = this.resolveRouteByPath(pathOnly)
              if (route && route.name) {
                tab.componentName = route.name
              }
            }
            if (tab.componentName) {
              this.$store.commit('ADD_CACHED_VIEW', tab.componentName)
            }
          })
        }
        if (savedActiveTab) {
          this.activeTab = savedActiveTab
        }
      } catch (e) {
        this.tabs = []
        this.activeTab = ''
      }
      if (this.$route.path !== '/login' && this.$route.path !== '/403' && this.$route.path !== '/404' && !this.isMobileStandalone && !this.isEmbedded) {
        this.addTabFromRoute(this.$route)
      }
    },

    // 根据路径查找路由配置
    resolveRouteByPath(path) {
      const routes = this.$router.options.routes || []
      for (const route of routes) {
        if (route.children && route.children.length > 0) {
          for (const child of route.children) {
            const fullPath = (route.path + '/' + child.path).replace(/\/+/g, '/')
            if (fullPath === path) {
              return child
            }
            // 处理动态路由（如 designer/:id? 或 inspection-record-detail/:id）
            if (child.path.includes(':')) {
              // :param? 表示可选参数，:param 表示必选参数
              const pattern = fullPath
                .replace(/:[^/]+\?/g, '(?:[^/]+)?')  // 可选参数
                .replace(/:[^/]+/g, '[^/]+')          // 必选参数
              const regex = new RegExp('^' + pattern + '$')
              if (regex.test(path)) {
                return child
              }
            }
          }
        } else if (route.path === path) {
          return route
        }
      }
      return null
    },

    addTabFromRoute(route) {
      // 用 fullPath 而非 path，保留 query 参数（如站点详情页的 ?id=xxx）
      // 这样切换 tab 时能恢复完整 URL，避免 query 丢失导致页面无法加载数据
      const fullPath = route.fullPath
      if (!fullPath || fullPath === '/') {
        return
      }
      // 去重时按 path（不含 query）匹配，避免同一页面不同 query 产生多个 tab
      const pathOnly = route.path
      const existingTab = this.tabs.find(tab => {
        // 兼容旧数据：tab.path 可能存的是 path 或 fullPath
        const tabPathOnly = tab.path.split('?')[0]
        return tabPathOnly === pathOnly
      })
      // 从路由 name 获取组件名（用于 keep-alive include）
      const componentName = route.name || ''
      if (!existingTab) {
        const menuInfo = this.findMenuInfo(this.menuList, pathOnly)
        if (menuInfo) {
          this.tabs.push({
            path: fullPath,
            title: menuInfo.title,
            icon: menuInfo.icon,
            componentName
          })
        } else if (route.meta && route.meta.title) {
          this.tabs.push({
            path: fullPath,
            title: route.meta.title,
            icon: route.meta.icon || 'el-icon-document',
            componentName
          })
        }
        // 新 tab 加入缓存
        if (componentName) {
          this.$store.commit('ADD_CACHED_VIEW', componentName)
        }
      } else {
        // tab 已存在，更新其 path 为最新的 fullPath（query 可能变化）
        existingTab.path = fullPath
        if (componentName && !existingTab.componentName) {
          existingTab.componentName = componentName
        }
      }
      this.activeTab = fullPath
    },

    // 初始化用户信息
    initUser() {
      console.log('initUser called')
      console.log('localStorage user:', localStorage.getItem('user'))
      console.log('localStorage token:', localStorage.getItem('token'))
      this.$store.dispatch('initUser')
      // 使用nextTick确保计算属性更新
      this.$nextTick(() => {
        console.log('After initUser - user:', this.user)
        console.log('After initUser - token:', this.$store.getters.token)
        // 无论用户是否登录，都加载菜单列表和动态路由
        console.log('Generating menu list and loading dynamic routes')
        import('./router').then(({ generateMenuList, loadDynamicRoutes }) => {
          const menuList = generateMenuList()
          console.log('Menu list:', menuList)
          this.$store.dispatch('setMenuList', menuList)
          // 加载动态路由
          const routes = loadDynamicRoutes()
          console.log('Loaded dynamic routes:', routes)
        })
      })
    },
    // 退出登录
    handleDropdownCommand(command) {
      if (command === 'changePassword') {
        this.changePasswordVisible = true
      } else if (command === 'logout') {
        this.handleLogout()
      }
    },
    async handleLogout() {
      try {
        await this.$axios.post('/auth/logout')
      } catch (e) {
        // ignore errors on logout
      }
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      sessionStorage.removeItem('app_tabs')
      sessionStorage.removeItem('app_active_tab')
      this.tabs = []
      this.activeTab = ''
      this.$store.dispatch('logout')
      this.$router.push('/login')
    },
    handleMenuSelect(key, keyPath) {
      console.log('Menu selected:', key, keyPath)
      
      const fullPath = key.startsWith('/') ? key : '/' + key
      
      if (this.$route.path === fullPath) {
        return
      }
      
      this.$router.push(fullPath)
    },
    
    // 查找菜单信息
    findMenuInfo(menuList, path) {
      // 移除路径开头的斜杠，确保匹配一致
      const cleanPath = path.startsWith('/') ? path.slice(1) : path
      
      for (const menu of menuList) {
        const menuPath = menu.path.startsWith('/') ? menu.path.slice(1) : menu.path
        if (menuPath === cleanPath && menu.meta) {
          return {
            title: menu.meta.title,
            icon: menu.meta.icon
          }
        }
        if (menu.children && menu.children.length > 0) {
          for (const subMenu of menu.children) {
            const subMenuPath = subMenu.path.startsWith('/') ? subMenu.path.slice(1) : subMenu.path
            const fullPath = menuPath + '/' + subMenuPath
            if (fullPath === cleanPath && subMenu.meta) {
              return {
                title: subMenu.meta.title,
                icon: subMenu.meta.icon
              }
            }
            if (subMenu.children && subMenu.children.length > 0) {
              for (const subSubMenu of subMenu.children) {
                const subSubMenuPath = subSubMenu.path.startsWith('/') ? subSubMenu.path.slice(1) : subSubMenu.path
                const fullSubPath = fullPath + '/' + subSubMenuPath
                if (fullSubPath === cleanPath && subSubMenu.meta) {
                  return {
                    title: subSubMenu.meta.title,
                    icon: subSubMenu.meta.icon
                  }
                }
              }
            }
          }
        }
      }
      return null
    },
    
    // 关闭标签页
    closeTab(tabPath) {
      const index = this.tabs.findIndex(tab => tab.path === tabPath)
      if (index !== -1) {
        const tab = this.tabs[index]
        // 从缓存中移除
        if (tab.componentName) {
          this.$store.commit('DEL_CACHED_VIEW', tab.componentName)
        }
        this.tabs.splice(index, 1)
        if (this.activeTab === tabPath && this.tabs.length > 0) {
          this.activeTab = this.tabs[this.tabs.length - 1].path
          this.$router.push(this.activeTab)
        } else if (this.tabs.length === 0) {
          this.activeTab = ''
        }
      }
    },

    closeAllTabs() {
      this.tabs = []
      this.activeTab = ''
      this.$store.commit('CLEAR_CACHED_VIEWS')
    },

    scrollTabs(direction) {
      const navScroll = this.$el.querySelector('.tab-scroll-wrapper .el-tabs__nav-scroll')
      if (!navScroll) return
      const scrollAmount = 200
      if (direction === 'left') {
        navScroll.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
      } else {
        navScroll.scrollBy({ left: scrollAmount, behavior: 'smooth' })
      }
    },
    
    // 切换标签页
    switchTab(tab) {
      // 处理el-tabs的tab-click事件参数
      let tabPath = ''
      if (typeof tab === 'string') {
        tabPath = tab
      } else if (tab && tab.props && tab.props.name) {
        tabPath = tab.props.name
      } else if (tab && tab.name) {
        tabPath = tab.name
      }
      if (tabPath) {
        this.activeTab = tabPath
        this.$router.push(tabPath)
      }
    },
    // 获取角色对应的CSS类名
    getRoleClass(roleName) {
      const roleClassMap = {
        '平台管理员': 'role-admin',
        '平台操作员': 'role-operator',
        '站点管理员': 'role-site-admin',
        '测试员': 'role-tester',
        '平台总管理': 'role-super-admin',
        '司机': 'role-driver',
        '站点运维': 'role-site-maintenance',
        '政府人员': 'role-government'
      }
      return roleClassMap[roleName] || 'role-default'
    }
  }
}
</script>

<style>
/* CSS变量定义 */
:root {
  --primary: #1890FF;
  --secondary: #13C2C2;
  --background: #ffffff;
  --text: #262626;
  --border: #E8E8E8;
  --sidebarBg: #ffffff;
  --topNavBg: #ffffff;
  --cardBg: #ffffff;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 登录容器样式 */
.login-container {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

/* 移动端独立页面容器样式 */
.mobile-standalone-container {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
}

/* 嵌入模式容器样式 */
.embedded-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--text);
}

/* 顶部菜单 */
.top-menu {
  background-color: var(--topNavBg);
  box-shadow: none;
  z-index: 5;
  overflow-x: hidden;
  white-space: nowrap;
}

/* 顶部菜单样式 */
.el-menu-demo {
  background-color: var(--topNavBg) !important;
  border: none !important;
  border-bottom: none !important;
  overflow-x: auto;
  white-space: nowrap;
  min-width: 0;
  flex-wrap: nowrap;
}

.el-menu-demo .el-menu-item {
  border: none !important;
  border-bottom: 2px solid transparent !important;
  text-align: center;
  padding: 0 16px;
}

.el-menu-demo .el-submenu .el-submenu__title {
  text-align: center;
}

.el-menu-demo .el-menu-item.is-active {
  border: none !important;
  border-bottom-color: var(--primary) !important;
  background-color: rgba(24, 144, 255, 0.05) !important;
  color: var(--primary) !important;
}

.el-menu-demo .el-submenu.is-active > .el-submenu__title {
  border: none !important;
  border-bottom-color: var(--primary) !important;
  background-color: rgba(24, 144, 255, 0.05) !important;
  color: var(--primary) !important;
}

/* 二级菜单样式 */
.el-menu-demo .el-submenu .el-menu {
  margin: 0 !important;
  padding: 0 !important;
  background-color: var(--topNavBg) !important;
  border: none !important;
}

/* 三级菜单样式 */
.el-menu-demo .el-submenu .el-submenu .el-menu {
  margin: 0 !important;
  padding: 0 !important;
  background-color: var(--topNavBg) !important;
  border: none !important;
}

/* 系统说明 */
.system-info {
  background-color: #ffffff;
  padding: 10px 24px;
  box-shadow: none;
  width: 100%;
  margin: 0;
  border: none;
}

.system-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  gap: 20px;
}

.system-logo {
  flex-shrink: 0;
  min-width: 260px;
}

.top-menu {
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
}

.system-actions {
  flex-shrink: 0;
  min-width: 200px;
}

/* 顶部菜单居中 */
.top-menu .el-menu-demo {
  display: flex;
  justify-content: center;
  width: 100%;
}

/* 主内容区 */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0;
}



.system-logo {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-icon {
  width: 100px;
  height: 48px;
  border-radius: 8px;
  object-fit: contain;
}

.logo-text h1 {
  font-size: 20px;
  font-weight: bold;
  color: #1890FF;
  margin: 0;
}

.logo-text p {
  font-size: 12px;
  color: #666;
  margin: 4px 0 0 0;
}

.system-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.system-actions .user-avatar {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  background-color:#edf2fa76;
}

.system-actions .user-avatar:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #262626;
  line-height: 1.2;
}

.user-role {
  font-size: 9px;
  font-weight: 500;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1.2;
  margin-top: 2px;
  white-space: nowrap;
}

/* 角色颜色定义 */
.role-admin {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.role-operator {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.role-site-admin {
  background-color: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

.role-tester {
  background-color: #fff1f0;
  color: #f5222d;
  border: 1px solid #ffa39e;
}

.role-super-admin {
  background-color: #f9f0ff;
  color: #722ed1;
  border: 1px solid #d3adf7;
}

.role-driver {
  background-color: #e6f7ff;
  color: #13c2c2;
  border: 1px solid #5cdbd3;
}

.role-site-maintenance {
  background-color: #fff7e6;
  color: #eb2f96;
  border: 1px solid #ffadd2;
}

.role-government {
  background-color: #f0f5ff;
  color: #2f54eb;
  border: 1px solid #adc6ff;
}

.role-default {
  background-color: #fafafa;
  color: #8c8c8c;
  border: 1px solid #d9d9d9;
}

/* 页面内容 */
.page-content {
  flex: 1;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  background-color: var(--background);
  border: none;
  box-shadow: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .system-logo h1 {
    font-size: 16px;
  }
  
  .system-actions {
    gap: 8px;
  }
  
  .user-avatar span {
    display: none;
  }
  
  .el-menu-demo .el-menu-item span,
  .el-menu-demo .el-submenu__title span {
    font-size: 14px;
  }
}

/* Element UI 主题适配 */
.el-menu {
  background-color: var(--topNavBg) !important;
  border: none !important;
  border-bottom: none !important;
  overflow: hidden !important;
}

.el-menu-item,
.el-submenu__title {
  color: var(--text) !important;
  border: none !important;
}

.el-menu-item:hover,
.el-submenu__title:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  border: none !important;
}

.el-menu-item.is-active {
  color: var(--primary) !important;
  background-color: rgba(24, 144, 255, 0.1) !important;
  border: none !important;
}

/* 顶部菜单特有样式 */
.el-menu--horizontal {
  border: none !important;
  border-bottom: none !important;
}

.el-menu--horizontal > .el-menu-item.is-active {
  border: none !important;
  border-bottom-color: var(--primary) !important;
  background-color: rgba(24, 144, 255, 0.05) !important;
}

.el-menu--horizontal > .el-submenu.is-active > .el-submenu__title {
  border: none !important;
  border-bottom-color: var(--primary) !important;
  color: var(--primary) !important;
}

.el-menu--horizontal > .el-submenu .el-submenu__title:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  border: none !important;
}

/* 标签页样式 - Chrome浏览器风格 */
.tab-container {
  display: flex !important;
  align-items: flex-end !important;
  background-color: #f1f3f5 !important;
  padding: 8px 0 0 10px !important;
  border-bottom: none !important;
  box-shadow: none !important;
  z-index: 4;
}

.tab-scroll-wrapper {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.tab-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: 34px;
  padding: 0 8px;
  background-color: #f1f3f5;
  border-bottom: none;
  gap: 2px;
}

.tab-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 4px;
  background-color: transparent;
  color: #5f6368;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  padding: 0;
  outline: none;
}

.tab-action-btn:hover {
  background-color: #dadce0;
  color: #202124;
}

.tab-action-btn:active {
  background-color: #c4c7c9;
}

.tab-container .el-tabs {
  margin-bottom: 0;
}

.tab-container .el-tabs__header {
  margin-bottom: 0 !important;
  border-bottom: none !important;
}

.tab-container .el-tabs__nav-wrap {
  padding: 0 !important;
  overflow: hidden !important;
}

.tab-container .el-tabs__nav-wrap::after {
  display: none !important;
}

.tab-container .el-tabs__nav-scroll {
  overflow-x: auto !important;
  overflow-y: hidden !important;
}

.tab-container .el-tabs__nav-scroll::-webkit-scrollbar {
  display: none;
}

.tab-container .el-tabs__nav {
  border: none !important;
  display: flex !important;
  gap: 0 !important;
}

.tab-container .el-tabs__item {
  height: 34px !important;
  line-height: 34px !important;
  padding: 0 16px !important;
  padding-right: 32px !important;
  font-size: 13px !important;
  color: #5f6368 !important;
  background-color: #e8eaed !important;
  border: none !important;
  border-radius: 8px 8px 0 0 !important;
  margin-right: 0 !important;
  margin-left: -1px !important;
  transition: background-color 0.15s ease, color 0.15s ease !important;
  position: relative !important;
  z-index: 1 !important;
  min-width: 80px !important;
  max-width: 200px !important;
}

.tab-container .el-tabs__item:first-child {
  margin-left: 0 !important;
}

.tab-container .el-tabs__item:hover {
  color: #202124 !important;
  background-color: #ecedee !important;
  z-index: 2 !important;
}

.tab-container .el-tabs__item.is-active {
  color: #202124 !important;
  background-color: #ffffff !important;
  z-index: 3 !important;
  font-weight: 500 !important;
  position: relative !important;
}

.tab-container .el-tabs__item.is-active::after {
  content: '' !important;
  position: absolute !important;
  bottom: -1px !important;
  left: 0 !important;
  right: 0 !important;
  height: 2px !important;
  background-color: #ffffff !important;
}

.tab-container .el-tabs__active-bar {
  display: none !important;
}

.tab-label {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-label i {
  font-size: 13px;
  flex-shrink: 0;
}

.tab-label span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-close {
  position: absolute !important;
  right: 6px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  margin-left: 0 !important;
  font-size: 14px !important;
  padding: 0 !important;
  width: 18px !important;
  height: 18px !important;
  line-height: 16px !important;
  text-align: center !important;
  border-radius: 50% !important;
  transition: all 0.15s ease !important;
  color: #80868b !important;
  opacity: 0 !important;
}

.tab-container .el-tabs__item:hover .tab-close,
.tab-container .el-tabs__item.is-active .tab-close {
  opacity: 1 !important;
}

.tab-close:hover {
  color: #202124 !important;
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.el-card {
  background-color: #f0f2f5 !important;
  border: 1px solid #ebeef5 !important;
}

.el-card__header {
  background-color: var(--cardBg) !important;
  border-bottom: none !important;
  color: var(--text) !important;
  padding: 10px 20px !important;
  margin-bottom: 0 !important;
}

.el-input__inner {
  background-color: var(--cardBg) !important;
  border: 1px solid var(--border) !important;
  color: var(--text) !important;
}

.el-button--primary {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
}

.el-dropdown-menu {
  background-color: var(--cardBg) !important;
  border: 1px solid var(--border) !important;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1) !important;
}

.el-dropdown-item {
  color: var(--text) !important;
}

.el-dropdown-item:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

.page-card .el-card__body {
  padding: 15px;
}

.page-card > .el-card__header {
  display: none;
}

.search-form {
  background-color: #fff;
  padding: 10px;
  border-radius: 6px;
  box-sizing: border-box;
}

.search-fields,
.row-search-fields {
  width: 100%;
}

.search-field-col,
.row-search-field-col {
  margin-bottom: 12px;
}

.search-field-col .el-form-item,
.row-search-field-col .el-form-item {
  display: flex;
  align-items: center;
  margin: 0;
  width: 100%;
  min-width: 0;
}

.search-field-col .el-form-item__label,
.row-search-field-col .el-form-item__label {
  flex: 0 0 auto;
  padding: 0 8px 0 0;
  color: #606266;
  font-size: 12px;
  line-height: 32px;
  font-weight: 400;
}

.search-field-col .el-form-item__content,
.row-search-field-col .el-form-item__content {
  flex: 1;
  min-width: 0;
  width: 100%;
  line-height: 32px;
}

.search-control {
  width: 100%;
}

.search-control .el-select,
.search-control .el-cascader,
.search-control .el-input {
  width: 100%;
}

.search-control .el-input__inner {
  height: 32px;
  line-height: 32px;
  border-color: #dcdfe6;
  border-radius: 3px;
  font-size: 12px;
}

.search-action-row {
  width: 100%;
}

.search-action-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
}

.search-action-right .el-button {
  height: 32px;
  padding: 8px 14px;
  border-radius: 3px;
  font-size: 12px;
  line-height: 1;
}

.search-action-right .el-button + .el-button {
  margin-left: 8px;
}

/* 按钮样式 */
.btn-search {
  color: #fff;
  background: #1890ff;
  border-color: #1890ff;
}

.btn-search:hover,
.btn-search:focus {
  color: #fff;
  background: #40a9ff;
  border-color: #40a9ff;
}

.btn-reset {
  color: #606266;
  background: #fff;
  border-color: #d9d9d9;
}

.btn-reset:hover,
.btn-reset:focus {
  color: #1890ff;
  background: #fff;
  border-color: #d9d9d9;
}

.btn-export {
  color: #1890ff;
  background: #fff;
  border-color: #1890ff;
}

.btn-export:hover,
.btn-export:focus {
  color: #40a9ff;
  background: #fff;
  border-color: #40a9ff;
}

.btn-add {
  color: #fff;
  background: #13c2c2;
  border-color: #13c2c2;
}

.btn-add:hover,
.btn-add:focus {
  color: #fff;
  background: #36cfc9;
  border-color: #36cfc9;
}

.el-table .el-button--small {
  margin: 0 2px;
}

.el-button--primary.is-plain {
  color: #1890FF;
  background: #fff;
  border-color: #1890FF;
}
.el-button--primary.is-plain:hover {
  background: #1890FF;
  color: #fff;
}

/* 搜索区域统一优化样式 */
.search-section {
  margin: 10px;
  padding: 10px;
  background-color: #ffffff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}


.search-section .form-conditions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.search-section .form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-section .form-actions-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.search-section .el-form-item {
  margin: 0 !important;
  display: flex;
  align-items: center;
}

.search-section .el-form-item__label {
  font-size: 13px;
  color: #606266;
  padding-right: 8px;
  line-height: 32px;
}

.search-section .el-input__inner,
.search-section .el-select .el-input__inner {
  height: 32px;
  line-height: 32px;
  font-size: 13px;
}

.search-section .el-button {
  height: 32px;
  padding: 0 16px;
  font-size: 13px;
}
.search-section .el-input__icon {
  height: 32px;
  line-height: 32px;
}
.search-action-right{
  display: flex;
  align-items: flex-end;
}
.row-search-fields{
  width: 100%;
}
.row-search-field-col,.search-field-col{
  padding-left: 30px;
}
.row-search-field-col:nth-child(1),
.row-search-field-col:nth-child(5),
.row-search-field-col:nth-child(10){
  padding-left: 0;
}
.search-field-col:nth-child(1),
.search-field-col:nth-child(5),
.search-field-col:nth-child(10){
  padding-left: 0;
}

/* 按钮样式 */
.btn-search {
  background-color: #1890FF !important;
  border-color: #1890FF !important;
  color: #fff !important;
}

.btn-search:hover {
  background-color: #40a9ff !important;
  border-color: #40a9ff !important;
}

.btn-reset {
  background-color: #fff !important;
  border-color: #d9d9d9 !important;
  color: #606266 !important;
}

.btn-reset:hover {
  color: #1890FF !important;
  border-color: #1890FF !important;
}

.btn-export {
  background-color: #fff !important;
  border-color: #1890FF !important;
  color: #1890FF !important;
}

.btn-export:hover {
  background-color: #e6f7ff !important;
}

.btn-add {
  background-color: #13C2C2 !important;
  border-color: #13C2C2 !important;
  color: #fff !important;
}

.btn-add:hover {
  background-color: #36cfc9 !important;
  border-color: #36cfc9 !important;
}

.content-section {
  margin: 10px;
  padding: 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #ebeef5;
}

</style>